import React, { useEffect, useRef } from "react";
import "../../styles/bh-modal.css";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared modal frame: overlay, header, scrolling body, optional sticky footer.
 * Desktop = centered card, mobile (<= 660px) = full-height sheet.
 */
function ModalShell({ title, subtitle, onClose, size = "md", footer, children, headerExtra, bodyClassName = "", bodyId }) {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // focus first field if the content did not autofocus something
    const t = setTimeout(() => {
      const dialog = dialogRef.current;
      if (dialog && !dialog.contains(document.activeElement)) {
        const first =
          dialog.querySelector("[data-autofocus]") ||
          dialog.querySelector("input:not([type='file']):not([type='hidden']), textarea");
        (first || dialog).focus();
      }
    }, 0);

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        // let the image lightbox (opened from the files modal) consume Esc first
        if (document.querySelector(".yarl__portal_open")) return;
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key === "Tab" && dialogRef.current) {
        const nodes = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE)).filter((n) => n.offsetParent !== null);
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, []);

  return (
    <div
      className='bh-modal-overlay'
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className={`bh-modal bh-modal--${size}`}
        role='dialog'
        aria-modal='true'
        aria-label={typeof title === "string" ? title : undefined}
        tabIndex={-1}
      >
        <header className='bh-modal__header'>
          <div className='bh-modal__titles'>
            <h3 className='bh-modal__title'>{title}</h3>
            {subtitle && <p className='bh-modal__subtitle'>{subtitle}</p>}
          </div>
          <button type='button' className='bh-icon-btn' onClick={onClose} aria-label='Close'>
            <i className='fa fa-times' aria-hidden='true'></i>
          </button>
        </header>
        {headerExtra && <div className='bh-modal__header-extra'>{headerExtra}</div>}
        <div id={bodyId} className={`bh-modal__body ${bodyClassName}`}>{children}</div>
        {footer && <footer className='bh-modal__footer'>{footer}</footer>}
      </div>
    </div>
  );
}

export default ModalShell;
